// Sijainnit — the building's physical install locations (e.g. Kattokonehuone, Kellari) used to
// group Talotekniikka. Restored from the old wizard's LocationsStep; lives in the Rakenne tab.
import { useEffect, useState } from "react";

import {
  getBuildingLocations,
  listZones,
  updateBuildingLocations,
  type Zone,
} from "@/lib/api/building";
import { inputGlowWrap, inputGlowInner, cardSubtle } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { SectionPanel } from "@/components/ui/section-panel";

export function LocationsEditor({
  buildingId,
  operator,
  refreshKey = 0,
  onChanged,
}: {
  buildingId: string;
  operator: boolean;
  refreshKey?: number;
  onChanged?: () => void;
}) {
  const [locations, setLocations] = useState<string[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [newLocation, setNewLocation] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getBuildingLocations(buildingId).catch((): string[] => []),
      listZones(buildingId).catch((): Zone[] => []),
    ]).then(([locs, zns]) => {
      if (cancelled) return;
      setLocations(locs);
      setZones(zns);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [buildingId, refreshKey]);

  async function save(updated: string[]) {
    setSaving(true);
    try {
      const result = await updateBuildingLocations(buildingId, updated);
      setLocations(result);
      onChanged?.();
    } finally {
      setSaving(false);
    }
  }

  async function addLocation(name?: string) {
    const trimmed = (name ?? newLocation).trim();
    if (!trimmed || locations.includes(trimmed)) return;
    if (!name) setNewLocation("");
    await save([...locations, trimmed]);
  }

  async function renameLocation(oldName: string) {
    const trimmed = editValue.trim();
    setEditing(null);
    if (!trimmed || trimmed === oldName || locations.includes(trimmed)) return;
    await save(locations.map((l) => (l === oldName ? trimmed : l)));
  }

  async function removeLocation(loc: string) {
    await save(locations.filter((l) => l !== loc));
  }

  const zoneSuggestions = zones.filter((z) => !locations.includes(z.name));

  return (
    <SectionPanel
      title="Sijainnit"
      subtitle="Fyysiset asennussijainnit talotekniikan ryhmittelyyn"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Ladataan…</p>
      ) : (
        <div className="max-w-lg space-y-4">
          <div className="space-y-2">
            {locations.length === 0 && (
              <p className="text-xs italic text-muted-foreground">
                Ei sijainteja.
              </p>
            )}
            {locations.map((loc) => (
              <div
                key={loc}
                className={cn(
                  cardSubtle,
                  "flex items-center gap-2 px-4 py-2.5",
                )}
              >
                {editing === loc ? (
                  <>
                    <div className={cn(inputGlowWrap, "flex-1")}>
                      <div className={inputGlowInner}>
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void renameLocation(loc);
                            if (e.key === "Escape") setEditing(null);
                          }}
                          className="w-full bg-transparent px-3 py-1.5 text-sm outline-none"
                        />
                      </div>
                    </div>
                    <button
                      disabled={saving}
                      onClick={() => void renameLocation(loc)}
                      className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">📍 {loc}</span>
                    {operator && (
                      <>
                        <button
                          disabled={saving}
                          onClick={() => {
                            setEditing(loc);
                            setEditValue(loc);
                          }}
                          className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                        >
                          Nimeä
                        </button>
                        <button
                          disabled={saving}
                          onClick={() => void removeLocation(loc)}
                          className="text-xs text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
                        >
                          Poista
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {operator && zoneSuggestions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Lisää vyöhyke sijainniksi
              </p>
              <div className="flex flex-wrap gap-1.5">
                {zoneSuggestions.map((z) => (
                  <button
                    key={z.id}
                    disabled={saving}
                    onClick={() => void addLocation(z.name)}
                    className="rounded-full border border-border/40 bg-card px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-50"
                  >
                    + {z.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {operator && (
            <div className="flex gap-2">
              <div className={cn(inputGlowWrap, "flex-1")}>
                <div className={inputGlowInner}>
                  <input
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void addLocation();
                    }}
                    placeholder="Uusi sijainti, esim. Kattokonehuone"
                    className="w-full bg-transparent px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <button
                disabled={saving || !newLocation.trim()}
                onClick={() => void addLocation()}
                className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
              >
                {saving ? "…" : "Lisää"}
              </button>
            </div>
          )}
        </div>
      )}
    </SectionPanel>
  );
}
